import { Link, useNavigate } from "@tanstack/react-router";
import { Eye, Heart, Minus, Plus, ShoppingCart } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ProductRow } from "@/integrations/supabase/db-types";
import { formatPrice, productSlug, resolveAvailability, availabilityColor, availabilityBadgeClass, productImageAlt } from "@/lib/format";
import { highlightText } from "@/lib/search-utils";
import { categorySlug } from "@/lib/category-icon";
import { ProductImage } from "@/components/storefront/ProductImage";
import { useWishlist } from "@/hooks/storefront/use-wishlist";
import { useCart } from "@/hooks/storefront/use-cart";
import {
  CART_DRAG_MIME,
  beginTouchDrag,
  flyToCart,
  hideCartDropHint,
  serializeDrag,
  showCartDropHint,
  setPlayfulProductDragImage,
} from "@/lib/fly-to-cart";
import { OfferCountdown } from "@/components/storefront/OfferCountdown";
import { BrandLogo } from "@/components/storefront/BrandLogo";
import { useBrandBadges } from "@/hooks/storefront/use-brand-logos";
import { useProductRating } from "@/hooks/storefront/use-review-summaries";
import { HealthScoreCircle, IngredientScoreCircle } from "@/components/storefront/HealthScore";
import { useIngredientScore } from "@/hooks/storefront/use-ingredient-scores";
import { RatingStars } from "@/components/storefront/RatingStars";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";

function stripHtml(html: string | null | undefined): string {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Skeleton that mirrors the exact structure of <ProductCard /> so that swapping
 * the placeholder for the real card produces zero layout shift (the image block
 * uses the same aspect-square ratio, so its height tracks the column width
 * identically to the real card).
 */
export function ProductCardSkeleton() {
  return (
    <article className="group relative flex flex-col rounded-lg border border-border bg-card p-3">
      <div className="mb-3 aspect-square animate-pulse rounded-md bg-muted" />
      <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
      <div className="mt-auto flex items-center justify-between pt-3">
        <div className="h-5 w-16 animate-pulse rounded bg-muted" />
        <div className="h-9 w-9 animate-pulse rounded-full bg-muted" />
      </div>
    </article>
  );
}

export function ProductCard({
  product,
  highlightTokens,
  priority,
  hideBrandBadge,
}: {
  product: ProductRow;
  highlightTokens?: string[];
  /** When true, the image loads eagerly with high fetch priority (LCP candidate). */
  priority?: boolean;
  /** Hide the brand logo badge (e.g. when the listing is already filtered to one brand). */
  hideBrandBadge?: boolean;
}) {
  const wishlist = useWishlist();
  const cart = useCart();
  const navigate = useNavigate();
  const brandBadges = useBrandBadges();
  const rating = useProductRating(product.id);
  const brand =
    !hideBrandBadge && product.manufacturer_id != null
      ? brandBadges.get(product.manufacturer_id)
      : undefined;
  const previewBrand =
    product.manufacturer_id != null
      ? brandBadges.get(product.manufacturer_id)
      : undefined;
  const [previewOpen, setPreviewOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const imgWrapRef = useRef<HTMLDivElement | null>(null);
  const draggedRef = useRef(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  useEffect(() => {
    setIsTouchDevice(
      typeof window !== "undefined" &&
        window.matchMedia("(pointer: coarse)").matches,
    );
  }, []);
  const slug = productSlug(product.name ?? "", product.ps_id, product.id);
  const inWishlist = wishlist.has(product.id);
  const inStock = product.stock_quantity > 0;

  const hasSale = product.original_price != null && product.original_price > product.price;
  const displayPrice = product.price;
  const originalPrice = product.original_price;
  const shortDescription = stripHtml(product.description_short || product.description);
  const availability = resolveAvailability(product.availability, product.stock_quantity);
  const availabilityStyle = availabilityColor(availability);
  const imageAlt = productImageAlt(product);
  const healthScore = (product as { health_score?: number | null }).health_score;
  const ingredientScore = useIngredientScore(healthScore == null ? product.id : undefined);
  const hasBadge = healthScore != null || ingredientScore != null;

  const dragPayload = {
    productId: product.id,
    psId: product.ps_id,
    name: product.name,
    price: Number(displayPrice),
    imageUrl: product.image_url,
    originalPrice: hasSale ? Number(originalPrice) : null,
    stock: product.stock_quantity,
    taxRate: Number((product as { tax_rate?: number | null }).tax_rate ?? 24),
    deposit: (() => {
      const d = Number((product as { bottle_deposit?: number | null }).bottle_deposit ?? 0);
      if (!(d > 0)) return null;
      const vat = Number((product as { tax_rate?: number | null }).tax_rate ?? 24);
      return d * (1 + vat / 100);
    })(),
  };

  const addWithFly = () => {
    const rect = imgWrapRef.current?.getBoundingClientRect();
    if (rect) {
      flyToCart({
        fromRect: rect,
        imageUrl: product.image_url,
        onArrive: () => cart.add(dragPayload),
      });
    } else {
      cart.add(dragPayload);
    }
  };

  const toggleWishlist = () =>
    wishlist.toggle({
      productId: product.id,
      psId: product.ps_id,
      name: product.name,
      price: Number(product.price),
      imageUrl: product.image_url,
      originalPrice: hasSale ? Number(originalPrice) : null,
    });

  return (
    <article
      className={`group relative z-10 flex flex-col rounded-lg border bg-card p-3 transition hover:shadow-md ${

        hasSale
          ? "mp-gold-card border-yellow-400/60"
          : "border-border hover:border-primary/40"
      } ${
        isDragging
          ? "scale-[0.97] opacity-80 ring-2 ring-dashed ring-primary/70"
          : ""
      }`}
    >
      {hasSale && (
        <>
          <span className="mp-sparkle" style={{ top: "6px", left: "10px" }}>✨</span>
          <span className="mp-sparkle" style={{ top: "40%", right: "12px", animationDelay: "0.6s" }}>⭐</span>
          <span className="mp-sparkle" style={{ bottom: "64px", left: "16px", animationDelay: "1.1s" }}>✨</span>
        </>
      )}
      {healthScore != null && (
        <div className="pointer-events-none absolute right-3 top-3 z-20">
          <HealthScoreCircle score={Number(healthScore)} />
        </div>
      )}
      {healthScore == null && ingredientScore != null && (
        <div className="pointer-events-none absolute right-3 top-3 z-20">
          <IngredientScoreCircle score={ingredientScore} />
        </div>
      )}
      <div data-card-preview="" className={`absolute right-2 z-10 hidden sm:grid ${hasBadge ? "top-14" : "top-2"}`}>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            setQty(1);
            setPreviewOpen(true);
          }}
          aria-label="Γρήγορη προεπισκόπηση"
          title="Γρήγορη προεπισκόπηση"
          className="mp-bouncy grid h-9 w-9 place-items-center rounded-full bg-background/95 text-muted-foreground shadow-md backdrop-blur transition-all duration-200 hover:scale-110 hover:text-primary opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
        >
          <Eye className="h-4 w-4" />
        </button>
      </div>

      <Link
        to="/p/$slug"
        params={{ slug }}
        className="flex flex-1 flex-col"
      >
        <div
          ref={imgWrapRef}
          draggable={inStock && !isTouchDevice}
          onDragStart={(e) => {
            if (!inStock) return;
            setIsDragging(true);
            e.dataTransfer.effectAllowed = "copy";
            const imgEl = (imgWrapRef.current?.querySelector(
              "img[data-product-image]",
            ) ?? null) as HTMLImageElement | null;
            setPlayfulProductDragImage(e.dataTransfer, dragPayload, imgEl);
            e.dataTransfer.setData(CART_DRAG_MIME, serializeDrag(dragPayload));
            e.dataTransfer.setData("text/plain", product.name);
            showCartDropHint();
          }}
          onDragEnd={() => {
            setIsDragging(false);
            hideCartDropHint();
          }}
          onContextMenu={(e) => {
            if (isTouchDevice) e.preventDefault();
          }}
          onTouchStart={(e) => {
            if (!inStock) return;
            const touch = e.touches[0];
            if (!touch) return;
            const imgEl = (imgWrapRef.current?.querySelector(
              "img[data-product-image]",
            ) ?? null) as HTMLImageElement | null;
            beginTouchDrag({
              touch,
              item: dragPayload,
              sourceImg: imgEl,
              onDragStateChange: (d) => {
                setIsDragging(d);
                if (d) {
                  draggedRef.current = true;
                } else {
                  // Ασφαλιστικό: αν δεν ακολουθήσει click, το flag δεν μένει
                  // «κολλημένο» και δεν καταπίνει το επόμενο tap.
                  window.setTimeout(() => {
                    draggedRef.current = false;
                  }, 400);
                }
              },
              onDrop: () => cart.add(dragPayload),
            });
          }}
          onClickCapture={(e) => {
            // Μετά από σύρσιμο με το δάχτυλο δεν ανοίγουμε τη σελίδα προϊόντος.
            if (draggedRef.current) {
              draggedRef.current = false;
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          style={isTouchDevice ? { touchAction: "pan-y", WebkitUserSelect: "none", WebkitTouchCallout: "none" } : undefined}
          className="relative mb-3 aspect-square overflow-hidden rounded-md bg-white cursor-grab active:cursor-grabbing select-none"
        >
          {availability && (
            <span className={`absolute left-2 top-2 z-10 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold shadow backdrop-blur-sm ${availabilityBadgeClass(availability)}`}>
              {availability}
            </span>
          )}
          <button
            data-card-wishlist=""
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleWishlist();
            }}
            onTouchStart={(e) => e.stopPropagation()}
            aria-label={inWishlist ? `Αφαίρεση ${product.name} από τα αγαπημένα` : `Προσθήκη ${product.name} στα αγαπημένα`}
            className="mp-bouncy absolute bottom-2 left-2 z-20 grid h-9 w-9 place-items-center rounded-full bg-background/95 text-muted-foreground opacity-100 shadow-md backdrop-blur transition-all duration-200 hover:scale-110 hover:text-primary cursor-pointer"
          >
            <Heart
              className={`h-4 w-4 ${inWishlist ? "fill-primary text-primary" : ""}`}
            />
          </button>
          {hasSale && (
            <div className="absolute bottom-2 left-[3.25rem] z-10">
              <OfferCountdown endsAt={product.offer_ends_at} compact />
            </div>
          )}
          {brand && (
            <span
              role="button"
              tabIndex={0}
              title={brand.name}
              aria-label={`Δες τα προϊόντα ${brand.name}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate({ to: "/brand/$id", params: { id: brand.slug } });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate({ to: "/brand/$id", params: { id: brand.slug } });
                }
              }}
              className="absolute bottom-1.5 right-1.5 z-20 grid h-9 w-14 cursor-pointer place-items-center overflow-hidden rounded-lg border border-border/70 bg-white/95 p-1 shadow-sm backdrop-blur transition hover:scale-105 hover:border-primary"
            >
              <BrandLogo
                logoUrl={brand.logo_url}
                name={brand.name}
                width={72}
                className="h-full w-full rounded-none border-0 bg-transparent"
                imgClassName="max-h-7 max-w-full"
              />
            </span>
          )}
          <ProductImage
            src={product.image_url}
            alt={imageAlt}
            width={260}
            quality={88}
            draggable={false}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : undefined}
            sizes="(min-width: 1024px) 11vw, (min-width: 640px) 26vw, 36vw"
            className="h-full w-full object-contain p-2 transition group-hover:scale-105"
          />
        </div>

        <h3 className="text-sm font-medium text-foreground group-hover:text-primary">
          {highlightTokens?.length ? highlightText(product.name, highlightTokens) : product.name}
        </h3>

        {rating && rating.count > 0 && (
          <span
            title={`${rating.average} στα 5 από ${rating.count} αξιολογήσεις`}
            aria-label={`Βαθμολογία ${rating.average} στα 5 από ${rating.count} αξιολογήσεις`}
            className="mt-1 inline-flex items-center gap-1 text-xs text-muted-foreground"
          >
            <RatingStars value={rating.average} size={12} />
            <span className="font-medium">{rating.average.toFixed(1).replace(".", ",")}</span>
            <span>({rating.count})</span>
          </span>
        )}

        <div className="mt-auto pt-3">
          <div className="flex items-center gap-2">
            <div className="flex flex-1 flex-col">
              {hasSale && (
                <span className="text-xs text-muted-foreground line-through">
                  {formatPrice(originalPrice ?? product.price)}
                </span>
              )}
              <span className={`font-bold ${hasSale ? "text-destructive" : "text-price"}`}>
                {formatPrice(displayPrice)}
              </span>
            </div>
            {inStock && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  addWithFly();
                }}
                aria-label="Προσθήκη στο καλάθι"
                title="Προσθήκη στο καλάθι"
                className="mp-bouncy grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-primary text-primary-foreground shadow-md transition hover:scale-110"
              >
                <Plus className="h-4 w-4" />
              </button>
            )}
          </div>
          {!inStock && (
            <div className="mt-1 text-xs text-muted-foreground">
              Εξαντλημένο
            </div>
          )}
        </div>
      </Link>

      {previewOpen && (
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-3xl overflow-hidden border-border/60 p-0 rounded-2xl">
          <div className="grid gap-0 md:grid-cols-2">
            <div className="relative flex items-center justify-center bg-white p-8">
              {hasSale && (
                <span className="absolute left-4 top-4 rounded-full bg-destructive px-3 py-1 text-xs font-semibold text-destructive-foreground shadow">
                  Προσφορά
                </span>
              )}
              {healthScore != null && (
                <div className="pointer-events-none absolute right-4 top-4 z-10">
                  <HealthScoreCircle score={Number(healthScore)} />
                </div>
              )}
              {healthScore == null && ingredientScore != null && (
                <div className="pointer-events-none absolute right-4 top-4 z-10">
                  <IngredientScoreCircle score={ingredientScore} />
                </div>
              )}
              <ProductImage
                src={product.image_url}
                alt={imageAlt}
                width={600}
                className="max-h-72 w-full object-contain"
                fallback={
                  <div className="grid h-72 w-full place-items-center text-6xl text-muted-foreground">
                    🐼
                  </div>
                }
              />
            </div>

            <div className="flex flex-col gap-4 p-6">
              <div className="flex flex-wrap items-center gap-2">
                {previewBrand ? (
                  <Link
                    to="/brand/$id"
                    params={{ id: previewBrand.slug }}
                    onClick={() => setPreviewOpen(false)}
                    title={previewBrand.name}
                    aria-label={`Δες τα προϊόντα ${previewBrand.name}`}
                    className="transition hover:scale-105"
                  >
                    <BrandLogo
                      logoUrl={previewBrand.logo_url}
                      name={previewBrand.name}
                      className="h-9 w-16 rounded-md border border-border/60 bg-white p-1"
                      imgClassName="h-full w-full object-contain"
                      width={96}
                    />
                  </Link>
                ) : (
                  product.manufacturer_name && (
                    <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {product.manufacturer_name}
                    </span>
                  )
                )}
                {availability && (
                  <span className={`ml-auto whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-bold shadow-sm ${availabilityBadgeClass(availability)}`}>
                    {availability}
                  </span>
                )}
              </div>
              {product.category_id != null && product.category_name && (
                <Link
                  to="/c/$slug"
                  params={{ slug: categorySlug(product.category_id, product.category_name) }}
                  onClick={() => setPreviewOpen(false)}
                  className="w-fit rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground transition hover:bg-primary/10 hover:text-primary"
                >
                  {product.category_name}
                </Link>
              )}
              <DialogTitle className="text-lg font-semibold leading-snug text-foreground">
                {product.name}
              </DialogTitle>

              {rating && rating.count > 0 && (
                <span
                  title={`${rating.average} στα 5 από ${rating.count} αξιολογήσεις`}
                  aria-label={`Βαθμολογία ${rating.average} στα 5 από ${rating.count} αξιολογήσεις`}
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground"
                >
                  <RatingStars value={rating.average} size={12} />
                  <span className="font-medium">{rating.average.toFixed(1).replace(".", ",")}</span>
                  <span>({rating.count})</span>
                </span>
              )}

              <div className="flex items-end gap-3">
                {hasSale && (
                  <span className="text-base text-muted-foreground line-through">
                    {formatPrice(originalPrice ?? product.price)}
                  </span>
                )}
                <span
                  className={`text-2xl font-bold ${hasSale ? "text-destructive" : "text-price"}`}
                >
                  {formatPrice(displayPrice)}
                </span>
              </div>

              {hasSale && (
                <OfferCountdown endsAt={product.offer_ends_at} />
              )}

              {shortDescription && (
                <p className="hidden md:block line-clamp-4 text-sm leading-relaxed text-muted-foreground">
                  {shortDescription}
                </p>
              )}

              {inStock ? (
                <div className="mt-auto flex flex-col gap-3 pt-2">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">Τεμάχια</span>
                    <div className="flex items-center rounded-full border border-border">
                      <button
                        type="button"
                        onClick={() => setQty((q) => Math.max(1, q - 1))}
                        aria-label="Μείωση"
                        className="grid h-9 w-9 place-items-center rounded-full text-muted-foreground transition hover:text-primary disabled:opacity-40"
                        disabled={qty <= 1}
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-8 text-center text-sm font-semibold">{qty}</span>
                      <button
                        type="button"
                        onClick={() => setQty((q) => (product.stock_quantity > 0 ? Math.min(product.stock_quantity, q + 1) : q + 1))}
                        aria-label="Αύξηση"
                        disabled={product.stock_quantity > 0 && qty >= product.stock_quantity}
                        className="grid h-9 w-9 place-items-center rounded-full text-muted-foreground transition hover:text-primary disabled:opacity-40"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      cart.add(dragPayload, qty);
                      setPreviewOpen(false);
                    }}
                    className="flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground shadow-md transition hover:scale-[1.02] hover:shadow-lg"
                  >
                    <ShoppingCart className="h-4 w-4" />
                    Προσθήκη στο καλάθι
                  </button>
                </div>
              ) : (
                <div className="mt-auto pt-2 text-sm font-medium text-muted-foreground">
                  Εξαντλημένο
                </div>
              )}

              <Link
                to="/p/$slug"
                params={{ slug }}
                onClick={() => setPreviewOpen(false)}
                className="text-sm font-medium text-primary hover:underline"
              >
                Δείτε όλες τις λεπτομέρειες →
              </Link>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      )}
    </article>
  );
}